import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Banner, Button, Flex, LoadingIndicator, Text, toast } from "@jsq/ds/core";
import { addAction, addError, startDurationVital, stopDurationVital } from "@jsq/observability";
import { FAY_CLASSIFICATION_VITAL, FAY_RUM_ACTIONS } from "#constants";
import { useDocumentIngestionQuery } from "#graphql/supergraphHooks";
import {
  useConfirmClassification,
  useReclassifyDocument,
  useRemoveDocumentFromIngestion,
} from "#hooks/useClassificationConfirm";
import { useStaffEntityNames } from "#hooks/useEntityNames";
import {
  buildEffectiveDoc,
  docTypeLabel,
  groupIntoPackages,
  packageTitle,
  type DocOverride,
  type EffectiveDoc,
} from "#utils/classificationGrouping";
import { ApprovePackageConfirmModal } from "../ApprovePackageConfirmModal";
import { ClassifyModal, type ClassifyValues } from "../ClassifyModal";
import { EditDocumentTypeModal } from "../EditDocumentTypeModal";
import { PackageGroupCard } from "../PackageGroupCard";
import { RemoveDocumentConfirmModal } from "../RemoveDocumentConfirmModal";
import * as styles from "./styles_vanilla.css";

type OverrideMap = Record<string, DocOverride>;

// Restore a single doc's overlay to its pre-edit value (dropping the key when
// there was none), used to roll back an optimistic edit after a failed write.
function restoreOverride(
  prev: OverrideMap,
  docId: string,
  prior: DocOverride | undefined,
): OverrideMap {
  const next = { ...prev };
  if (prior === undefined) {
    delete next[docId];
  } else {
    next[docId] = prior;
  }
  return next;
}

type ModalState =
  | { kind: "none" }
  | { kind: "classify"; mode: "assign" | "move"; docIds: string[] }
  | { kind: "editType"; docId: string }
  | { kind: "remove"; docId: string }
  | { kind: "approve" };

export type ClassificationConfirmProps = {
  ingestionId: string;
  readOnly?: boolean;
  /** Fired after `approveIngestionClassification` succeeds. The parent uses it
   *  to close the (opaque) drawer and refresh the coverage grid so newly-created
   *  deliverable packages appear immediately. */
  onApproved?: () => void;
  /** Arena the ingestion read and every classification write are scoped to.
   *  Undefined until the host app resolves it, which skips the read. */
  arenaId: number | undefined;
};

/**
 * ClassificationConfirm — the confirm/approve screen. Reads the durable
 * ingestion via `documentIngestion(id)` and groups documents into collapsible
 * deliverable-package cards with a pinned "Not classified" bucket. Assign /
 * move / edit-type / remove run through per-document modals; the batch approves
 * in a single action once every document is classified.
 *
 * In `readOnly` mode it renders the terminal view for a COMPLETED ingestion: the
 * package/file summary only, with no per-document actions or Approve button.
 */
export const ClassificationConfirm: React.FC<ClassificationConfirmProps> = ({
  ingestionId,
  readOnly = false,
  onApproved,
  arenaId,
}) => {
  // The read is arena-scoped server-side (fay requires arenaId); skip until a
  // valid arena resolves so we never issue the required arg as NaN.
  const { data, loading, error } = useDocumentIngestionQuery({
    variables: { arenaId: arenaId!, id: ingestionId },
    fetchPolicy: "cache-and-network",
    skip: arenaId === undefined,
  });

  const { reclassify } = useReclassifyDocument(arenaId);
  const { remove, loading: removing } = useRemoveDocumentFromIngestion(arenaId);
  const { confirm, loading: confirming } = useConfirmClassification(arenaId);

  // Writes persist for real (reclassify/remove/approve are federated mutations),
  // but we don't refetch documentIngestion afterward, so user assignments/edits
  // live in a local optimistic overlay that drives the grouped UI in-session. On
  // reload the durable read model is re-read and the entity name is backfilled —
  // fay persists entityId but returns entityName: null.
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  // Count of reclassify writes still in flight. Edits apply optimistically and
  // fire without awaiting, so this gates Approve until they settle — otherwise a
  // user could approve a batch before the last edit is persisted, and the
  // terminal approve binds server-persisted state that wouldn't include it yet.
  const [pendingWrites, setPendingWrites] = useState(0);

  // Per-ingestion state (overrides/removed/modal/pendingWrites) resets for free
  // because Slideout mounts this component with `key={ingestionId}`, so a new
  // ingestion is a fresh mount — no reset effect to keep in sync.
  const ingestion = data?.documentIngestion ?? null;

  // The auto-classified entityIds in this ingestion. The entity picker searches
  // server-side and loads no full universe, so we resolve the name for exactly
  // these ids instead, ensuring we never leak the raw id.
  const ingestionEntityIds = useMemo(
    () =>
      (ingestion?.documents ?? [])
        .map((d) => d.classification.entityId)
        .filter((id): id is string => !!id),
    [ingestion],
  );
  // entityId -> name, so buildEffectiveDoc can backfill the name fay drops on a
  // refetch (it persists only entityId, returning entityName: null).
  const { names: entityNames } = useStaffEntityNames(ingestionEntityIds, arenaId);

  const effectiveDocs = useMemo<EffectiveDoc[]>(
    () =>
      (ingestion?.documents ?? [])
        .filter((d) => !removed.has(d.id))
        .map((d) => buildEffectiveDoc(d, overrides[d.id], entityNames)),
    [ingestion, removed, overrides, entityNames],
  );

  const groups = useMemo(() => groupIntoPackages(effectiveDocs), [effectiveDocs]);
  const unclassifiedCount = groups.find((g) => g.unclassified)?.docs.length ?? 0;
  const allClassified = effectiveDocs.length > 0 && unclassifiedCount === 0;

  const total = effectiveDocs.length;
  const classifiedGroups = groups.filter((g) => !g.unclassified);
  const packageCount = classifiedGroups.length;
  const classifiedCount = total - unclassifiedCount;

  // Times how long the user takes to decide. Slideout keys this component on the
  // ingestion id, so each ingestion gets its own measurement. A read-only view
  // presents no decision to time. An abandoned screen never stops the vital and
  // so reports nothing — correct, because the accept rate comes from the decision
  // actions, not from the vital.
  useEffect(() => {
    if (readOnly) {
      return;
    }
    startDurationVital(FAY_CLASSIFICATION_VITAL, {
      description: "Fay classification decision",
      context: { ingestion_id: ingestionId },
    });
  }, [readOnly, ingestionId]);

  const decisionTimed = useRef(false);
  // A clear is a decision too, so the vital stops on whichever comes first;
  // every decision after that still reports as its own action.
  const recordDecision = useCallback(
    (decision: "confirm" | "clear", documentCount: number) => {
      addAction(FAY_RUM_ACTIONS.classificationDecision, {
        decision,
        document_count: documentCount,
        ingestion_id: ingestionId,
      });
      if (!decisionTimed.current) {
        decisionTimed.current = true;
        stopDurationVital(FAY_CLASSIFICATION_VITAL);
      }
    },
    [ingestionId],
  );

  const closeModal = useCallback(() => setModal({ kind: "none" }), []);

  const handleClassifyConfirm = useCallback(
    (values: ClassifyValues) => {
      if (modal.kind !== "classify") {
        return;
      }
      const { docIds } = modal;
      // Snapshot the prior override per doc so a failed write can be rolled back.
      const priorByDoc: OverrideMap = Object.fromEntries(
        docIds.map((docId) => [docId, overrides[docId]]),
      );
      setOverrides((prev) => {
        const next = { ...prev };
        for (const docId of docIds) {
          next[docId] = {
            ...prev[docId],
            entityId: values.entityId,
            entityName: values.entityName,
            reportingDate: values.reportingDate,
            userAssigned: true,
          };
        }
        return next;
      });
      closeModal();
      for (const docId of docIds) {
        setPendingWrites((n) => n + 1);
        reclassify({
          documentId: docId,
          entityId: values.entityId,
          reportingDate: values.reportingDate,
        })
          .catch(() => {
            // The hook already toasts; restore just this doc's prior overlay so
            // the UI doesn't show a classification the server rejected.
            setOverrides((prev) => restoreOverride(prev, docId, priorByDoc[docId]));
          })
          .finally(() => setPendingWrites((n) => n - 1));
      }
    },
    [modal, overrides, reclassify, closeModal],
  );

  const handleEditTypeConfirm = useCallback(
    (doc: EffectiveDoc, documentType: string) => {
      const prior = overrides[doc.id];
      setOverrides((prev) => ({
        ...prev,
        [doc.id]: { ...prev[doc.id], documentType },
      }));
      closeModal();
      setPendingWrites((n) => n + 1);
      reclassify({
        documentId: doc.id,
        documentType,
        entityId: doc.entityId,
        reportingDate: doc.reportingDate,
      })
        .catch(() => {
          setOverrides((prev) => restoreOverride(prev, doc.id, prior));
        })
        .finally(() => setPendingWrites((n) => n - 1));
    },
    [overrides, reclassify, closeModal],
  );

  const handleRemoveConfirm = useCallback(async () => {
    if (modal.kind !== "remove") {
      return;
    }
    const { docId } = modal;
    try {
      // Removes are awaited (pessimistic): the doc only leaves the batch once the
      // server confirms. On failure the hook already toasts; keep the doc in the
      // batch and the modal open rather than leaving an unhandled rejection.
      await remove(docId);
      setRemoved((prev) => new Set(prev).add(docId));
      recordDecision("clear", 1);
      closeModal();
    } catch (err) {
      // Intentionally swallowed for the user — surfaced via the hook's toast —
      // but the failure still has to reach error tracking.
      addError(err, { fay_operation: "classification_clear" });
    }
  }, [modal, remove, closeModal, recordDecision]);

  const handleApprove = useCallback(async () => {
    const ids = effectiveDocs.map((d) => d.id);
    const res = await confirm(ingestionId, ids);
    if (res.ok) {
      const noun = ids.length === 1 ? "document" : "documents";
      toast.success(`Approved classification of ${ids.length} ${noun}`);
      recordDecision("confirm", ids.length);
      onApproved?.();
    } else {
      // `confirm` resolves rather than rejects, so the cause is already gone;
      // a synthetic error keeps the failure visible in error tracking without
      // copying server text into the payload.
      addError(new Error("Fay classification approve failed"), {
        fay_operation: "classification_confirm",
      });
    }
  }, [confirm, ingestionId, effectiveDocs, onApproved, recordDecision]);

  // The confirmation modal's Approve action: run the existing approve flow, then
  // close the modal either way — `finally` so a thrown rejection (e.g. the
  // arena getter throwing before confirm()'s own try/catch) can't leave the
  // modal stuck open with no user feedback.
  const handleApproveConfirm = useCallback(async () => {
    try {
      await handleApprove();
    } finally {
      closeModal();
    }
  }, [handleApprove, closeModal]);

  if (loading && !data) {
    return (
      <Flex alignItems="center" gap={8}>
        <LoadingIndicator size="small" />
        <Text variant="regular-md" asSpan>
          Loading classifications…
        </Text>
      </Flex>
    );
  }

  // Hard-fail only when there's nothing to show. Under cache-and-network a
  // background refetch can error while the cache still holds a usable read model;
  // in that case keep rendering the cached view rather than wiping it for a banner.
  if (error && !data) {
    return (
      <Banner
        variant="danger"
        title="Couldn’t load classifications"
        description={error.message}
      />
    );
  }

  if (!ingestion || effectiveDocs.length === 0) {
    return (
      <Flex justifyContent="center">
        <Text variant="regular-md">No documents to review.</Text>
      </Flex>
    );
  }

  const busy = confirming || removing || pendingWrites > 0;
  const singleDocId = modal.kind === "editType" || modal.kind === "remove" ? modal.docId : null;
  const modalDoc = singleDocId
    ? (effectiveDocs.find((d) => d.id === singleDocId) ?? null)
    : null;
  const classifyDocs =
    modal.kind === "classify"
      ? modal.docIds
          .map((id) => effectiveDocs.find((d) => d.id === id))
          .filter((d): d is EffectiveDoc => !!d)
      : [];

  return (
    <Flex flexDirection="column" gap={12}>
      {!readOnly &&
        groups.map((group) => (
          <PackageGroupCard
            key={group.key}
            group={group}
            onAssign={(d) => setModal({ kind: "classify", mode: "assign", docIds: [d.id] })}
            onBulkAssign={(docs) =>
              setModal({ kind: "classify", mode: "assign", docIds: docs.map((d) => d.id) })
            }
            onMove={(d) => setModal({ kind: "classify", mode: "move", docIds: [d.id] })}
            onEditDocType={(d) => setModal({ kind: "editType", docId: d.id })}
            onRemove={(d) => setModal({ kind: "remove", docId: d.id })}
            disabled={busy}
          />
        ))}

      <Flex alignItems="center" gap={8}>
        <Avatar name="Admin oversight agent" size="sm" />
        <Text variant="strong-md" asSpan>
          Admin oversight agent
        </Text>
      </Flex>

      <Flex flexDirection="column" gap={12} className={styles.summaryBox}>
        <Text variant="regular-md">
          {readOnly ? "Approved " : "Classified "}
          {readOnly ? total : `${classifiedCount} of ${total}`}{" "}
          {total === 1 ? "document" : "documents"} into {packageCount} deliverable{" "}
          {packageCount === 1 ? "package" : "packages"}:
        </Text>
        {allClassified || readOnly ? (
          <Flex flexDirection="column" gap={8}>
            {classifiedGroups.map((g) => (
              <Flex key={g.key} flexDirection="column" gap={2}>
                <Text variant="regular-md">
                  • {packageTitle(g)} ({g.docs.length} file{g.docs.length === 1 ? "" : "s"})
                </Text>
                {g.docs.map((d) => (
                  <Text key={d.id} variant="secondary-sm" className={styles.summaryFileLine}>
                    {d.filename} — {docTypeLabel(d.documentType) ?? "Unclassified type"}
                  </Text>
                ))}
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text variant="regular-md">
            Assign a deliverable package to the remaining {unclassifiedCount}{" "}
            {unclassifiedCount === 1 ? "document" : "documents"} to continue.
          </Text>
        )}
        {!readOnly && (
          <Flex alignItems="center">
            <Button
              variant="primary"
              onClick={() => setModal({ kind: "approve" })}
              disabled={busy || !allClassified}
            >
              Approve classification of {effectiveDocs.length}{" "}
              {effectiveDocs.length === 1 ? "document" : "documents"}
            </Button>
          </Flex>
        )}
      </Flex>

      <ClassifyModal
        isOpen={modal.kind === "classify"}
        mode={modal.kind === "classify" ? modal.mode : "assign"}
        docs={classifyDocs}
        onClose={closeModal}
        onConfirm={handleClassifyConfirm}
        arenaId={arenaId}
      />
      <EditDocumentTypeModal
        isOpen={modal.kind === "editType"}
        doc={modal.kind === "editType" ? modalDoc : null}
        onClose={closeModal}
        onConfirm={handleEditTypeConfirm}
      />
      <RemoveDocumentConfirmModal
        isOpen={modal.kind === "remove"}
        doc={modal.kind === "remove" ? modalDoc : null}
        onClose={closeModal}
        // Both confirm handlers swallow their own failures (surfaced by the
        // hooks' toasts), so the promises are intentionally unawaited.
        onConfirm={() => void handleRemoveConfirm()}
        loading={removing}
      />
      <ApprovePackageConfirmModal
        isOpen={modal.kind === "approve"}
        documentCount={effectiveDocs.length}
        packageCount={packageCount}
        onClose={closeModal}
        onConfirm={() => void handleApproveConfirm()}
        loading={confirming}
      />
    </Flex>
  );
};
